//@flow
import React, {useEffect, useState} from 'react';

import {IconButton, makeStyles, Typography} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';

import {
  useDeleteToggleProductSelection,
  useSelectedProductActions,
  useSelectedProducts,
  useSelectedProductsVariants,
} from '../../utils/bulkEditOptions';
import BulkEditOptionItem from './BulkEditOptionItem';
import CustomHorizontalScroll from '../../../../elements/CustomHorizontalScroll';
import BulkEditNewOfert from './BulkEditNewOfert';
import BulkEditNewStock from './BulkEditNewStock';
import BulkEditNewPrice from './BulkEditNewPrice';
import BulkEditNewCategory from './BulkEditNewCategory';

import {useToggle} from '../../../../utils/react';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';
import {useAlert} from '../../../../elements/Alert/AlertContext';
import {useCategories} from '../../../categories/utils/index.js';

import TrashIcon from '../../../../elements/icons/TrashIcon';
import MoneyArchiveIcon from '../../../../elements/icons/MoneyArchiveIcon';
import MoneySquareIcon from '../../../../elements/icons/MoneySquareIcon';
import ViewGridIcon from '../../../../elements/icons/ViewGridIcon';
import PackagesIcon from '../../../../elements/icons/PackagesIcon';
import XmarkIcon from '../../../../elements/icons/XmarkIcon';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: '.5rem',
    background: '#F4F4F6',
    boxShadow: '0px -4px 12px rgba(150, 145, 145, 0.25)',
    position: 'sticky',
    right: 0,
    minHeight: '120px',
    width: '100%',
    padding: '2rem',
    bottom: '64px',
    [theme.breakpoints.up('lg')]: {
      bottom: 0,
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: '1rem',

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
  },
  title: {
    color: colors.grey900,
    fontSize: '1rem',
    fontWeight: 600,
  },
  scrollContainer: {
    display: 'flex',
    gap: '1rem',
    padding: '.25rem 0',
  },
  widthCustom: {
    width: 'calc(100% - 300px)',

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  widthCustomOptions: {
    width: 'calc(100% - 50px)',
  },
  scrollArrow: {
    padding: '.75rem',
  },
  icon: {
    width: '2.2rem',
    height: '2.2rem',
  },
  productQuantitySelected: {
    fontWeight: 600,
    width: '230px',
  },
  circlePurple: {
    width: '0.8rem',
    height: '0.8rem',
    borderRadius: '50%',
    background: colors.primary,
  },
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: 'fit-content',

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  iconButton: {
    background: 'white',
  },
  iconButtonDesktop: {
    background: 'white',
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'block',
    },
  },
  iconButtonMobile: {
    background: 'white',
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
}));

type showOptionBulk = 'NONE' | 'ALL' | 'CATEGORY' | 'OFERT' | 'STOCK' | 'PRICE';

export default function BulkEditOptions() {
  const classes = useStyles();
  const {showAlert} = useAlert();
  const categories = useCategories();

  const productSelection = useSelectedProducts();
  const selectedProductVariants = useSelectedProductsVariants();
  const removeAllToggleProductSelection = useDeleteToggleProductSelection();

  const [confirmModalOpen, toggleConfirmModalOpen] = useToggle();
  const {deleteSelectedProducts} = useSelectedProductActions();

  const [showBulkOption, setShowBulkOption] = useState<showOptionBulk>('ALL');

  const deleteProducts = () => {
    deleteSelectedProducts()
      .then(() => {
        showAlert('Productos eliminados', {type: 'success'});
        removeAllToggleProductSelection();
      })
      .catch(() => {
        showAlert('Error al eliminar productos', {type: 'error'});
      })
      .finally(() => {
        toggleConfirmModalOpen();
      });
  };

  const handleDeleteProducts = () => {
    toggleConfirmModalOpen();
  };

  const productCount = productSelection.length;
  const variantCount = selectedProductVariants.length;

  useEffect(() => {
    if (!productCount && !variantCount) {
      setShowBulkOption('ALL');
    }
  }, [productCount, variantCount]);

  if (!productSelection.length && !selectedProductVariants.length) return null;

  return (
    <aside className={classes.root}>
      <header className={classes.header}>
        {showBulkOption === 'ALL' && (
          <div className={classes.flexCenter}>
            <div className={classes.flexCenter}>
              <div className={classes.circlePurple}></div>
              <div>
                <Typography className={classes.productQuantitySelected}>
                  {productCount} producto{productCount > 1 ? 's' : ''}{' '}
                  seleccionado
                  {productCount > 1 ? 's' : ''}
                </Typography>

                <Typography className={classes.productQuantitySelected}>
                  {variantCount} variante
                  {variantCount > 1 ? 's' : ''} seleccionada
                  {variantCount > 1 ? 's' : ''}
                </Typography>
              </div>
            </div>
            <IconButton
              className={classes.iconButtonMobile}
              onClick={() => {
                removeAllToggleProductSelection();
                setShowBulkOption('ALL');
              }}
            >
              <XmarkIcon />
            </IconButton>
          </div>
        )}
        {showBulkOption === 'ALL' && (
          <CustomHorizontalScroll
            classes={{
              scrollContainer: classes.scrollContainer,
              arrowButton: classes.scrollArrow,
              root: classes.widthCustom,
            }}
          >
            <BulkEditOptionItem
              icon={<MoneySquareIcon />}
              title="Precio nuevo"
              onClick={() => {
                setShowBulkOption('PRICE');
              }}
            />
            <BulkEditOptionItem
              icon={<MoneyArchiveIcon />}
              title="Precio de Oferta nuevo"
              onClick={() => {
                setShowBulkOption('OFERT');
              }}
            />
            <BulkEditOptionItem
              icon={<ViewGridIcon />}
              title="Categoría nueva"
              onClick={() => {
                setShowBulkOption('CATEGORY');
              }}
            />
            <BulkEditOptionItem
              icon={<PackagesIcon />}
              title="Stock nuevo"
              onClick={() => {
                setShowBulkOption('STOCK');
              }}
            />
            {productSelection.length > 0 && (
              <BulkEditOptionItem
                icon={<TrashIcon />}
                title="Eliminar productos"
                color="danger"
                disabled={productSelection.length < 1}
                onClick={handleDeleteProducts}
              />
            )}
          </CustomHorizontalScroll>
        )}

        {showBulkOption === 'CATEGORY' && (
          <BulkEditNewCategory
            // peerPagePagination={peerPagePagination}
            // page={page}
            categories={categories}
            productCount={productCount}
            variantCount={variantCount}
            setShowBulkOption={setShowBulkOption}
            removeAllToggleProductSelection={removeAllToggleProductSelection}
          />
        )}

        {showBulkOption === 'OFERT' && (
          <BulkEditNewOfert
            // peerPagePagination={peerPagePagination}
            // page={page}
            productCount={productCount}
            variantCount={variantCount}
            setShowBulkOption={setShowBulkOption}
            removeAllToggleProductSelection={removeAllToggleProductSelection}
          />
        )}

        {showBulkOption === 'STOCK' && (
          <BulkEditNewStock
            // peerPagePagination={peerPagePagination}
            // page={page}
            productCount={productCount}
            variantCount={variantCount}
            setShowBulkOption={setShowBulkOption}
            removeAllToggleProductSelection={removeAllToggleProductSelection}
          />
        )}

        {showBulkOption === 'PRICE' && (
          <BulkEditNewPrice
            // peerPagePagination={peerPagePagination}
            // page={page}
            productCount={productCount}
            variantCount={variantCount}
            setShowBulkOption={setShowBulkOption}
            removeAllToggleProductSelection={removeAllToggleProductSelection}
          />
        )}

        {showBulkOption === 'ALL' && (
          <IconButton
            className={classes.iconButtonDesktop}
            onClick={() => {
              removeAllToggleProductSelection();
              setShowBulkOption('ALL');
            }}
          >
            <XmarkIcon />
          </IconButton>
        )}
      </header>

      <ConfirmModal
        open={confirmModalOpen}
        onClose={toggleConfirmModalOpen}
        onConfirm={deleteProducts}
        onCancel={toggleConfirmModalOpen}
        message="Los productos seleccionados serán eliminados"
        title="¿Estás seguro de esta acción?"
      ></ConfirmModal>
    </aside>
  );
}
